export class PutStreamMachine {
  #chunkSize = 48 * 1024; // 48 KB per message
  #offset = 0;
  #completionSent = false;
  #putId: number|undefined = undefined;
  #data: Buffer
  #path: string = 'new'
  errorHandler: (reason: any) => void = () => {}
  resolveHandler: (putId: number) => void = () => {}

  public constructor(
    readonly transId: number,
    destPath: string|undefined,
    private app_metadata: string|undefined,
    data: Buffer|string,
    private webSocket: WebSocket,
  ) {
    if (typeof data === 'string') {
      const numberArray = data.split(',').map(Number);
      const arrowBuffer = new Uint8Array(numberArray);
      this.#data = Buffer.from(arrowBuffer);
      this.#path = destPath || 'new'
    } else {
      this.#data = data;
      // Set path even when data is a Buffer
      this.#path = destPath || 'new';
    }
  }

  /// client calls this to send the initial put with metadata
  start(put_id: number|undefined = undefined) {
    this.#putId = put_id
    const json = {
        message: 'PutStream',
        trans_id: this.transId,
        path: this.#path,
        put_type: 'Stream',
        app_metadata: this.app_metadata,
        put_id
    }
    this.webSocket.send(JSON.stringify(json))
  }

  /// client calls when a response related to the put is received
  handleResponse(response: any) {
    switch (response.message) {
      case 'StartBinary':
        this.#sendNextChunk()
        break
      case 'Continue':
        this.#sendNextChunk()
        break
      case 'PutComplete':
        if (this.#putId === undefined && response.app_metadata) {
          const meta = JSON.parse(response.app_metadata)
          if (meta && meta.end_command && meta.end_command.put_id) {
            this.#putId = meta.end_command.put_id as number
          }
        }
        console.log('Data transmission completed successfully.', this.#putId)
        this.resolveHandler(this.#putId||-1) //should always be defined
        break
      case 'Error':
        this.errorHandler(response.description)
        break
    }
  }

  #sendNextChunk() {
    const chunk_len = this.#data.length - this.#offset
    const size = Math.min(this.#chunkSize, chunk_len)

    // All data sent → send the terminal frame: a 20-byte header (trans_id only).
    // ehq-server's datafusion_ws PUT handler recognizes a frame of EXACTLY 20
    // bytes as the end marker (file_operations.rs: `if data.len() == 20`); a
    // 30-byte frame is treated as another data chunk → it replies `Continue` →
    // we re-send → infinite "completion marker" loop and the PUT never resolves.
    // Guard so we send the terminal once: after it the server replies
    // PutComplete, but a stray Continue must not re-enter and flood the socket.
    if (size <= 0 || this.#offset >= this.#data.length) {
      if (this.#completionSent) {
        return
      }
      this.#completionSent = true
      console.log('Sending 20-byte completion signal')
      this.webSocket.send(this.#transAsBuffer())
      return
    }

    const chunk = this.#data.slice(this.#offset, this.#offset + size)
    console.log(`Sending chunk of size ${chunk.length}`)

    // Chunk frame: [20-byte trans_id header][data]. The server strips the first
    // 20 bytes (`data.slice(20..)`) and writes the rest, so the header MUST be
    // exactly 20 bytes — a wider header corrupts the Arrow IPC stream.
    const header = this.#transAsBuffer()
    const combined = Buffer.concat([header, chunk])
    this.webSocket.send(combined)

    this.#offset += size
  }

  // 20-byte binary header: zero-padded trans_id only. Matches enoch's
  // put-stream.ts and the ehq-server datafusion_ws frame contract.
  #transAsBuffer() {
    return Buffer.from(Number(this.transId).toString().padStart(20, '0'))
  }

}

